import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  FileText, Upload, Trash2, Loader2, Download, CheckCircle2, ArrowLeft, File as FileIcon,
} from "lucide-react";

const DOC_TYPES = [
  { key: "cv", label: "CV", desc: "Votre CV à jour (PDF de préférence).", required: true },
  { key: "diploma", label: "Diplôme", desc: "Diplôme de pharmacien / préparateur.", required: true },
  { key: "id_card", label: "Pièce d'identité", desc: "CNI ou passeport (recto/verso si CNI).", required: false },
  { key: "rpps", label: "Carte RPPS / ADELI", desc: "Numéro RPPS pour les pharmaciens.", required: false },
  { key: "practice_authorization", label: "Attestation d'autorisation d'exercer", desc: "Attestation d'inscription à l'Ordre / autorisation d'exercer.", required: false, requiredForPharmacist: true },
];

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_MB = 8;

function humanSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function MyDocuments() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const docs = user?.documents || {};
  const isPharmacist = (user?.professional_role || "").toLowerCase() === "pharmacien";
  const docTypes = DOC_TYPES.map((d) => (d.requiredForPharmacist ? { ...d, required: isPharmacist } : d));
  const completed = docTypes.filter((d) => docs[d.key]?.url).length;

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-8 pb-24 lg:pb-10">
      <button onClick={() => nav("/profile")} className="text-xs text-[#6B726A] hover:text-[#1A4D2E] inline-flex items-center gap-1 mb-3" data-testid="back-to-profile">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour au profil
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Mes documents</h1>
          <p className="text-sm text-[#6B726A] mt-1">Ces documents seront partagés avec les pharmacies qui vous proposent une mission.</p>
        </div>
        <div className="text-xs px-3 py-1 rounded-full bg-[#E8F0EA] text-[#1A4D2E] border border-[#1A4D2E]/15" data-testid="docs-progress">
          {completed}/{docTypes.length} documents fournis
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {docTypes.map((d) => (
          <DocumentCard key={d.key} def={d} doc={docs[d.key]} onChange={refreshUser} />
        ))}
      </div>

      <div className="mt-8 bg-[#FFF8E6] border border-[#F2C46B]/40 rounded-2xl p-4 text-xs text-[#7a5b18]">
        🔒 Vos documents sont stockés de manière sécurisée. Seules les pharmacies avec lesquelles vous interagissez peuvent y accéder.
      </div>
    </div>
  );
}

function DocumentCard({ def, doc, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okFlash, setOkFlash] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const open = () => fileRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`Fichier trop volumineux (max ${MAX_MB} Mo)`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", def.key);
      fd.append("file", f);
      await api.post("/auth/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onChange();
      setOkFlash(true);
      setTimeout(() => setOkFlash(false), 1800);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/auth/documents/${def.key}`);
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  const hasFile = !!doc?.url;
  const isImage = (doc?.content_type || "").startsWith("image/");
  const fullUrl = hasFile ? (doc.url.startsWith("http") ? doc.url : `${BACKEND_URL}${doc.url}`) : null;

  return (
    <div className={`bg-white border rounded-2xl p-5 transition-colors ${hasFile ? "border-[#1A4D2E]/30" : "border-[#E2E5E0]"}`} data-testid={`doc-card-${def.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>
            {def.label}
            {def.required && <span className="text-[10px] uppercase tracking-wider text-[#E3655B] font-semibold">Requis</span>}
          </div>
          <p className="text-xs text-[#6B726A] mt-0.5">{def.desc}</p>
        </div>
        {hasFile ? (
          <span className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E8F0EA] text-[#1A4D2E]" data-testid={`doc-status-${def.key}`}>
            <CheckCircle2 className="w-3 h-3" /> Fourni
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F7F8F6] text-[#6B726A]" data-testid={`doc-status-${def.key}`}>Manquant</span>
        )}
      </div>

      {hasFile ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F7F8F6] border border-[#E2E5E0]">
            <div className="w-10 h-12 rounded bg-white border border-[#E2E5E0] grid place-items-center overflow-hidden shrink-0">
              {isImage ? <img src={fullUrl} alt="" className="w-full h-full object-cover" /> : <FileIcon className="w-5 h-5 text-[#1A4D2E]" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-[#0F2A1B] truncate" title={doc.original_name}>{doc.original_name}</div>
              <div className="text-[11px] text-[#6B726A]">{humanSize(doc.size)} · {(doc.content_type || "").split("/").pop()}</div>
            </div>
            <a href={fullUrl} target="_blank" rel="noreferrer" data-testid={`doc-download-${def.key}`}
              className="w-8 h-8 rounded-lg border border-[#E2E5E0] grid place-items-center text-[#1A4D2E] hover:bg-white" aria-label="Voir">
              <Download className="w-4 h-4" />
            </a>
          </div>
          <div className="flex gap-2">
            <button onClick={open} disabled={busy} data-testid={`doc-replace-${def.key}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-[#1A4D2E] text-[#1A4D2E] text-xs font-medium hover:bg-[#E8F0EA] disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Remplacer
            </button>
            <button onClick={remove} disabled={busy} data-testid={`doc-remove-${def.key}`}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-[#E2E5E0] text-[#E3655B] text-xs font-medium hover:bg-[#FFF4F3] disabled:opacity-60">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button onClick={open} disabled={busy} data-testid={`doc-upload-${def.key}`}
          className="w-full border-2 border-dashed border-[#E2E5E0] rounded-xl p-6 text-center hover:bg-[#F7F8F6] disabled:opacity-60 transition-colors">
          {busy ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#1A4D2E] mx-auto" />
          ) : (
            <>
              <Upload className="w-5 h-5 text-[#1A4D2E] mx-auto mb-1" />
              <div className="text-xs font-medium text-[#1A4D2E]">Téléverser</div>
              <div className="text-[10px] text-[#6B726A] mt-0.5">PDF, JPG, PNG · {MAX_MB} Mo max</div>
            </>
          )}
        </button>
      )}

      {err && <div className="mt-2 text-xs text-[#E3655B]" data-testid={`doc-error-${def.key}`}>{err}</div>}
      {okFlash && <div className="mt-2 text-xs text-[#1A4D2E] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Document enregistré</div>}

      <input ref={fileRef} type="file" accept={ACCEPT} onChange={upload} className="hidden" data-testid={`doc-input-${def.key}`} />
    </div>
  );
}
